import { Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Sparkles, Bug, Calendar, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

export default function Changelog() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();

    const updates = [
        {
            version: "1.3.0",
            date: t('changelog.updates.v130.date'),
            title: t('changelog.updates.v130.title'),
            type: "major",
            changes: [
                t('changelog.updates.v130.change1'),
                t('changelog.updates.v130.change2'),
                t('changelog.updates.v130.change3'),
                t('changelog.updates.v130.change4')
            ]
        },
        {
            version: "1.2.0",
            date: t('changelog.updates.v120.date'),
            title: t('changelog.updates.v120.title'),
            type: "major",
            changes: [
                t('changelog.updates.v120.change1'),
                t('changelog.updates.v120.change2'),
                t('changelog.updates.v120.change3'),
                t('changelog.updates.v120.change4')
            ]
        },
        {
            version: "1.1.0",
            date: t('changelog.updates.v110.date'),
            title: t('changelog.updates.v110.title'),
            type: "minor",
            changes: [
                t('changelog.updates.v110.change1'),
                t('changelog.updates.v110.change2'),
                t('changelog.updates.v110.change3')
            ]
        },
        {
            version: "1.0.0",
            date: t('changelog.updates.v100.date'),
            title: t('changelog.updates.v100.title'),
            type: "major",
            changes: [
                t('changelog.updates.v100.change1'),
                t('changelog.updates.v100.change2'),
                t('changelog.updates.v100.change3'),
                t('changelog.updates.v100.change4')
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans">
            <SEO
                title={t('changelog.title')}
                description={t('changelog.seo_description')}
                canonical="/changelog"
            />
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-2xl font-bold text-text-main">CitoyenPro</span>
                    </Link>
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </nav>

            <main className="pt-32 pb-24 px-6">
                <div className="max-w-3xl mx-auto">
                    <Link to="/" className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-8">
                        <ArrowLeft size={20} />
                        {t('changelog.nav.back_home')}
                    </Link>

                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-purple-500/10 text-purple-500 mb-6">
                            <Rocket size={40} />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
                            {t('changelog.hero.title')}
                        </h1>
                        <p className="text-xl text-text-muted max-w-xl mx-auto">
                            {t('changelog.hero.subtitle')}
                        </p>
                    </div>

                    <div className="space-y-12 relative before:absolute before:left-[19px] before:top-4 before:bottom-4 before:w-0.5 before:bg-dark-border">
                        {updates.map((update, i) => (
                            <div key={i} className="relative pl-12">
                                {/* Timeline Dot */}
                                <div className={`absolute left-0 top-1 w-10 h-10 rounded-full border-4 border-dark-bg flex items-center justify-center z-10 ${update.type === 'major' ? 'bg-primary text-white' : 'bg-dark-card border-dark-border text-text-muted'
                                    }`}>
                                    {update.type === 'major' ? <Sparkles size={16} /> : <Bug size={16} />}
                                </div>

                                <div className="bg-dark-card border border-dark-border rounded-3xl p-8 hover:border-primary/30 transition-all">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className="font-mono text-sm font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                                                    v{update.version}
                                                </span>
                                                <span className="text-sm text-text-muted flex items-center gap-1">
                                                    <Calendar size={14} />
                                                    {update.date}
                                                </span>
                                            </div>
                                            <h2 className="text-2xl font-bold text-text-main">{update.title}</h2>
                                        </div>
                                    </div>

                                    <ul className="space-y-3">
                                        {update.changes.map((change, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-text-muted">
                                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                                                <span className="leading-relaxed">{change}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
